import React, { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import { observer } from "mobx-react-lite";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import { useStore } from "../../../stores/store";
import { useSetMenu } from "../../../hooks/useSetMenu";

const MAForecastGraph = observer(() => {
  const axiosPrivate = useAxiosPrivate();
  const { mainStore } = useStore();
  const svgRef = useRef(null);
  const svgRef2 = useRef(null);

  const [historical, setHistorical] = useState([]);
  const [forecast, setForecast] = useState([]);
  const [aggregatedData, setAggregatedData] = useState([]);
  useSetMenu(mainStore.MAForecastGraphMenuType, mainStore.MAForecastGraphMenuNo);
  useEffect(() => {
    const fetchForecast = async () => {
      try {
        const response = await axiosPrivate.get('/clustering/forecastMAActivity?horizon=5');
        console.log("🔍 Full API Response:", response.data);
  
        setHistorical(response.data.historical || []);
        setForecast(response.data.forecast || []);
        const groupedData = aggregateByTechPeriod(response.data.historical || []);
        setAggregatedData(groupedData);
        if (!response.data.forecast || response.data.forecast.length === 0) {
          console.warn("⚠️ No forecast data received!");
        }
      } catch (error) {
        console.error('❌ API Fetch Error:', error);
      }
    };
    fetchForecast();
  }, [axiosPrivate]);
  
  const aggregateByTechPeriod = (data) => {
    const techPeriods = [
      { period: "1970 - 1979", range: [1970, 1979] },
      { period: "1980 - 1989", range: [1980, 1989] },
      { period: "1990 - 1999", range: [1990, 1999] },
      { period: "2000 - 2009", range: [2000, 2009] },
      { period: "2010 - 2019", range: [2010, 2019] },
      { period: "2020 - Present", range: [2020, 2030] },
    ];
  
    return techPeriods.map((tp) => {
      const count = data
        .filter((d) => d.year >= tp.range[0] && d.year <= tp.range[1])
        .reduce((sum, d) => sum + d.count, 0);
      return { period: tp.period, count };
    });
  };

  useEffect(() => {
    if (historical.length > 0 || forecast.length > 0) {
      drawChart();
    }
  }, [historical, forecast]);

  useEffect(() => {
    if (aggregatedData.length > 0) {
      drawSecondGraph();
    }
  }, [aggregatedData]);

  useEffect(() => {
    const tooltip = d3.select("body")
      .append("div")
      .attr("class", "tooltip")
      .style("position", "absolute")
      .style("background", "white")
      .style("padding", "8px")
      .style("border-radius", "4px")
      .style("box-shadow", "0px 0px 5px rgba(0,0,0,0.3)")
      .style("opacity", 0)
      .style("pointer-events", "none"); 
  }, []);
  

  const techEvents = {
    "1970 - 1979": "Moore’s Law (Microprocessors)",
    "1980 - 1989": "System Integration (Networking, OS)",
    "1990 - 1999": "Internet Boom (Web, E-Commerce)",
    "2000 - 2009": "Cloud Computing (AWS, SaaS)",
    "2010 - 2019": "AI & Automation (Deep Learning, IoT)",
    "2020 - Present": "Quantum Computing & AI (5G, Edge AI)",
  };
  
  const drawSecondGraph = () => {
    const margin = { top: 30, right: 150, bottom: 80, left: 80 };
    const width = 800 - margin.left - margin.right;
    const height = 400 - margin.top - margin.bottom;
    const svg = d3.select(svgRef2.current);
    svg.selectAll("*").remove();
  
    const xScale = d3.scaleBand().domain(aggregatedData.map((d) => d.period)).range([0, width]).padding(0.4);
    const yScale = d3.scaleLinear().domain([0, d3.max(aggregatedData, (d) => d.count) * 1.2]).range([height, 0]).nice();
  
    const g = svg.append("g").attr("transform", `translate(${margin.left}, ${margin.top})`);
  

    const yEventScale = d3
      .scaleBand()
      .domain(aggregatedData.map((d) => techEvents[d.period]))
      .range([height, 0])
      .padding(0.4);
  
    g.append("g")
      .attr("class", "y-tech-axis")
      .attr("transform", `translate(${width + 20}, 0)`)
      .call(d3.axisRight(yEventScale))
      .selectAll("text")
      .style("fill", "black")
      .style("font-weight", "bold")
      .style("font-size", "12px");
  

    g.append("g")
      .attr("transform", `translate(0, ${height})`)
      .call(d3.axisBottom(xScale))
      .selectAll("text")
      .style("text-anchor", "end")
      .attr("dx", "-0.8em")
      .attr("dy", "0.15em")
      .attr("transform", "rotate(-25)");
  

    g.append("g").call(d3.axisLeft(yScale));
  

    const bars = g
      .selectAll(".bar")
      .data(aggregatedData)
      .enter()
      .append("rect")
      .attr("x", (d) => xScale(d.period))
      .attr("y", height)
      .attr("width", xScale.bandwidth())
      .attr("height", 0)
      .attr("fill", "url(#gradient)")
      .on("mouseover", function (event, d) {
        d3.select(this).transition().duration(200).attr("fill", "darkblue");
      
        d3.select(".tooltip")
          .style("opacity", 1)
          .html(
            `<strong>Tech Period:</strong> ${d.period}<br>
            <strong>M&A Count:</strong> ${d.count}<br>
            <strong>Technological Event:</strong> ${techEvents[d.period] || "Unknown"}`
          )
          .style("left", event.pageX + 10 + "px")
          .style("top", event.pageY - 40 + "px");
      })
      .on("mousemove", function (event) {
        d3.select(".tooltip")
          .style("left", event.pageX + 10 + "px")
          .style("top", event.pageY - 40 + "px");
      })
      .on("mouseout", function () {
        d3.select(this).transition().duration(200).attr("fill", "url(#gradient)");
      
        d3.select(".tooltip")
          .style("opacity", 0);
      })
  
    bars.transition()
      .duration(800)
      .attr("y", (d) => yScale(d.count))
      .attr("height", (d) => height - yScale(d.count));
  

    g.selectAll(".bar-label")
      .data(aggregatedData)
      .enter()
      .append("text")
      .attr("class", "bar-label")
      .attr("x", (d) => xScale(d.period) + xScale.bandwidth() / 2)
      .attr("y", (d) => yScale(d.count) - 5)
      .attr("text-anchor", "middle")
      .attr("fill", "black")
      .style("font-weight", "bold")
      .text((d) => d.count);
  

    svg
      .append("defs")
      .append("linearGradient")
      .attr("id", "gradient")
      .attr("x1", "0%")
      .attr("x2", "0%")
      .attr("y1", "0%")
      .attr("y2", "100%")
      .html(`
        <stop offset="0%" stop-color="steelblue" />
        <stop offset="100%" stop-color="lightblue" />
      `);
  

    g.append("text")
      .attr("x", width / 2)
      .attr("y", height + 60)
      .attr("text-anchor", "middle")
      .attr("font-size", "14px")
      .text("Technological Periods");
  

    g.append("text")
      .attr("transform", "rotate(-90)")
      .attr("y", -60)
      .attr("x", -height / 2)
      .attr("text-anchor", "middle")
      .attr("font-size", "14px")
      .text("Total M&A Count");
  
  };
  
  
  
  
  

  const drawChart = () => {
    const margin = { top: 30, right: 60, bottom: 50, left: 60 };
    const width = 800 - margin.left - margin.right;
    const height = 450 - margin.top - margin.bottom;

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    svg.attr("width", width + margin.left + margin.right).attr("height", height + margin.top + margin.bottom);

    const g = svg.append("g").attr("transform", `translate(${margin.left}, ${margin.top})`);

    const mAndAData = historical.map((d) => ({
      year: +d.year,
      count: +d.count,
      techImpactScore: +d.techImpactScore,
      techImpactName: d.techImpactName,

    }));

    const forecastData = forecast.map((f) => ({
      year: +f.year,
      forecast: +f.forecast,
      lower: +f.lower,
      upper: +f.upper,
    }));

    const allYears = [...mAndAData, ...forecastData];

    const xScale = d3
      .scaleLinear()
      .domain(d3.extent(allYears, (d) => d.year))
      .range([0, width]);

    const yScaleLeft = d3
      .scaleLinear()
      .domain([0, d3.max(allYears, (d) => d.count || d.forecast) * 1.2])
      .range([height, 0])
      .nice();

    const yScaleRight = d3
      .scaleLinear()
      .domain([0, d3.max(mAndAData, (d) => d.techImpactScore) * 1.2])
      .range([height, 0])
      .nice();

    g.append("g")
      .attr("transform", `translate(0, ${height})`)
      .call(d3.axisBottom(xScale).tickFormat(d3.format("d")));

    g.append("g")
      .attr("class", "y-axis-left")
      .call(d3.axisLeft(yScaleLeft))
      .append("text")
      .attr("fill", "steelblue")
      .attr("x", -height / 2)
      .attr("y", -40)
      .attr("transform", "rotate(-90)")
      .attr("text-anchor", "middle")
      .text("Number of M&As");

    g.append("g")
      .attr("class", "y-axis-right")
      .attr("transform", `translate(${width}, 0)`)
      .call(d3.axisRight(yScaleRight))
      .append("text")
      .attr("fill", "green")
      .attr("x", height / 2)
      .attr("y", 50)
      .attr("transform", "rotate(-90)")
      .attr("text-anchor", "middle")
      .text("Tech Impact Score");

    const lineMAndA = d3
      .line()
      .x((d) => xScale(d.year))
      .y((d) => yScaleLeft(d.count));

    g.append("path")
      .datum(mAndAData)
      .attr("fill", "none")
      .attr("stroke", "steelblue")
      .attr("stroke-width", 2)
      .attr("d", lineMAndA);

    const lineTechImpact = d3
      .line()
      .x((d) => xScale(d.year))
      .y((d) => yScaleRight(d.techImpactScore))
      .curve(d3.curveBasis);

    g.append("path")
      .datum(mAndAData)
      .attr("fill", "none")
      .attr("stroke", "green")
      .attr("stroke-width", 2)
      .style("stroke-dasharray", "4,2")
      .attr("d", lineTechImpact);

    const lineForecast = d3
      .line()
      .x((d) => xScale(d.year))
      .y((d) => yScaleLeft(d.forecast));

    g.append("path")
      .datum(forecastData)
      .attr("fill", "none")
      .attr("stroke", "orange")
      .attr("stroke-width", 2)
      .style("stroke-dasharray", "5,3")
      .attr("d", lineForecast);

    const areaConfidence = d3
      .area()
      .x((d) => xScale(d.year))
      .y0((d) => yScaleLeft(d.lower))
      .y1((d) => yScaleLeft(d.upper));

    g.append("path")
      .datum(forecastData)
      .attr("fill", "orange")
      .attr("opacity", 0.2)
      .attr("d", areaConfidence);

    g.selectAll(".mAndA-circle")
      .data(mAndAData)
      .enter()
      .append("circle")
      .attr("class", "mAndA-circle")
      .attr("cx", (d) => xScale(d.year))
      .attr("cy", (d) => yScaleLeft(d.count))
      .attr("r", 4)
      .attr("fill", "steelblue")
      .append("title")
      .text((d) => `Year: ${d.year}\nM&A Count: ${d.count}`);

    g.selectAll(".tech-circle")
    .data(mAndAData)
    .enter()
    .append("circle")
    .attr("class", "tech-circle")
    .attr("cx", (d) => xScale(d.year))
    .attr("cy", (d) => yScaleRight(d.techImpactScore))
    .attr("r", 4)
    .attr("fill", "green")
    .on("mouseover", function (event, d) {
      d3.select(this).transition().duration(200).attr("r", 6).attr("fill", "darkgreen");
  
      d3.select(".tooltip")
        .style("opacity", 1)
        .html(
          `<strong>Year:</strong> ${d.year}<br>
           <strong>Tech Impact Score:</strong> ${d.techImpactScore}<br>
           <strong>Technological Event:</strong> ${d.techImpactName || "Unknown"}`
        )
        .style("left", event.pageX + 10 + "px")
        .style("top", event.pageY - 40 + "px");
    })
    .on("mousemove", function (event) {
      d3.select(".tooltip")
        .style("left", event.pageX + 10 + "px")
        .style("top", event.pageY - 40 + "px");
    })
    .on("mouseout", function () {
      d3.select(this).transition().duration(200).attr("r", 4).attr("fill", "green");
  
      d3.select(".tooltip").style("opacity", 0);
    });
  

    g.selectAll(".forecast-circle")
      .data(forecastData)
      .enter()
      .append("circle")
      .attr("class", "forecast-circle")
      .attr("cx", (d) => xScale(d.year))
      .attr("cy", (d) => yScaleLeft(d.forecast))
      .attr("r", 4)
      .attr("fill", "orange")
      .append("title")
      .text((d) => `Year: ${d.year}\nForecast: ${d.forecast.toFixed(1)}`);
  };

  return (
    <div>
      <h2>M&A Trend Forecast with Technological Impact</h2>
      <svg ref={svgRef} />
      <h2>Aggregated M&A by Tech Period</h2>
<svg ref={svgRef2} width="800" height="400" />

    </div>
  );
});

export default MAForecastGraph;
